import { Sprite, Texture } from "pixi.js";
import { CENTRE, PLOTS, ROAD, VILLAGE_RADIUS_PX } from "../village/layout.js";
import { gridToScreen } from "./iso.js";

/** A single private surface: soft clearing, connected limestone paths and planted foundations. */
export function villageGround(material: HTMLImageElement): Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 1024;
  const g = canvas.getContext("2d")!;
  const extent = 370;
  const centre = gridToScreen(CENTRE, CENTRE);
  const k = canvas.width / (extent * 2);
  g.scale(k, k);
  g.translate(extent, extent - centre.y);
  g.drawImage(material, -extent, centre.y - extent, extent * 2, extent * 2);
  g.fillStyle = "#84926855";
  g.fillRect(-extent, centre.y - extent, extent * 2, extent * 2);
  const shade = g.createRadialGradient(0, centre.y - 60, 90, 0, centre.y, extent);
  shade.addColorStop(0, "#d9c89812");
  shade.addColorStop(0.72, "#35534915");
  shade.addColorStop(1, "#66795330");
  g.fillStyle = shade;
  g.fillRect(-extent, centre.y - extent, extent * 2, extent * 2);
  const path = new Path2D();
  const roadPoints = ROAD.map(([x, y], i) => {
    const at = gridToScreen(x, y);
    if (i === 0 || i === ROAD.length - 1) return at;
    const before = gridToScreen(...ROAD[i - 1]!);
    const after = gridToScreen(...ROAD[i + 1]!);
    return { x: (before.x + at.x * 2 + after.x) / 4, y: at.y };
  });
  roadPoints.forEach((at, i) => {
    if (i === 0) path.moveTo(at.x, at.y);
    else path.lineTo(at.x, at.y);
  });
  g.lineCap = "round";
  g.lineJoin = "round";
  // Decorative footpaths find gaps BETWEEN fixed plots. This never edits ROAD.
  const occupied = new Set(
    PLOTS.flatMap((p) =>
      Array.from({ length: p.w * p.d }, (_, i) => `${p.x + (i % p.w)},${p.y + Math.floor(i / p.w)}`)
    )
  );
  const connected = new Set(ROAD.map(([x, y]) => `${x},${y}`));
  for (const plot of PLOTS) {
    if (plot.id === "main_building" || plot.id === "wall") continue;
    const start = `${plot.x + plot.w},${plot.y + plot.d - 1}`;
    const previous = new Map<string, string | null>([[start, null]]);
    const queue = [start];
    let end: string | undefined;
    for (let i = 0; i < queue.length && i < 1200; i++) {
      const key = queue[i]!;
      if (connected.has(key)) {
        end = key;
        break;
      }
      const [x, y] = key.split(",").map(Number) as [number, number];
      for (const [dx, dy] of [
        [1, 0],
        [0, 1],
        [-1, 0],
        [0, -1],
      ] as const) {
        const nx = x + dx,
          ny = y + dy,
          next = `${nx},${ny}`;
        if (nx < 0 || ny < 0 || nx > 22 || ny > 22 || occupied.has(next) || previous.has(next))
          continue;
        previous.set(next, key);
        queue.push(next);
      }
    }
    if (!end) continue;
    const branch = new Path2D();
    let first = true;
    for (let key: string | null = end; key; key = previous.get(key) ?? null) {
      connected.add(key);
      const [x, y] = key.split(",").map(Number) as [number, number];
      const p = gridToScreen(x, y);
      if (first) branch.moveTo(p.x, p.y);
      else branch.lineTo(p.x, p.y);
      first = false;
    }
    g.strokeStyle = "#5b583e38";
    g.lineWidth = 17;
    g.stroke(branch);
    g.strokeStyle = "#b4ab88";
    g.lineWidth = 12;
    g.stroke(branch);
    g.strokeStyle = "#d0c4a05c";
    g.lineWidth = 7;
    g.stroke(branch);
  }
  for (const [width, color] of [
    [40, "#514b3528"],
    [35, "#a99a77"],
    [32, "#c6ba98"],
  ] as const) {
    g.lineWidth = width;
    g.strokeStyle = color;
    g.stroke(path);
  }
  // Sparse paving joints make this a lane rather than a flat ribbon.
  g.strokeStyle = "#96866660";
  g.lineWidth = 0.7;
  for (let i = 1; i < roadPoints.length; i++) {
    const p = roadPoints[i]!;
    g.beginPath();
    g.moveTo(p.x - 12, p.y);
    g.lineTo(p.x + 12, p.y + 1);
    g.stroke();
  }
  // Each plot has a quiet worn-earth apron, with its shadow cast down-right.
  for (const plot of PLOTS) {
    const p = gridToScreen(plot.x + (plot.w - 1) / 2, plot.y + (plot.d - 1) / 2);
    const radius = 22 + (plot.w + plot.d) * 6;
    const worn = g.createRadialGradient(p.x, p.y, 6, p.x, p.y, radius);
    worn.addColorStop(0, "#b9ac7f85");
    worn.addColorStop(1, "#b9ac7f00");
    g.fillStyle = worn;
    g.fillRect(p.x - radius, p.y - radius, radius * 2, radius * 2);
    g.fillStyle = "#243d3333";
    g.beginPath();
    g.ellipse(p.x + 8, p.y + 4, radius * 0.75, radius * 0.3, 0, 0, Math.PI * 2);
    g.fill();
  }
  // A feathered forest floor replaces the hard, saw-toothed tile perimeter.
  g.globalCompositeOperation = "destination-in";
  const fade = g.createRadialGradient(0, centre.y, VILLAGE_RADIUS_PX + 25, 0, centre.y, extent);
  fade.addColorStop(0, "#fff");
  fade.addColorStop(1, "#ffffff00");
  g.fillStyle = fade;
  g.fillRect(-extent, centre.y - extent, extent * 2, extent * 2);
  const sprite = new Sprite(Texture.from(canvas));
  sprite.position.set(-extent, centre.y - extent);
  sprite.scale.set(1 / k);
  sprite.eventMode = "none";
  return sprite;
}
